import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Navbar } from '../components/layout/Navbar';
import { MobileNav } from '../components/layout/MobileNav';
import Footer from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomPage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/public/pages/${slug}`);
      if (!response.ok) {
        throw new Error('Page not found');
      }
      const data = await response.json();
      setPage(data);
    } catch (err) {
      setError(language === 'fr' ? 'Page non trouvée' : 'Page not found');
    } finally {
      setLoading(false);
    }
  }, [slug, language]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Simple markdown-like rendering
  const renderContent = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    const elements = [];
    let inList = false;
    let listItems = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc pl-6 mb-4 space-y-1">
            {listItems.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    lines.forEach((line, index) => {
      // Headers
      if (line.startsWith('# ')) {
        flushList();
        elements.push(<h1 key={index} className="text-3xl font-bold mb-6 mt-4">{line.substring(2)}</h1>);
      } else if (line.startsWith('## ')) {
        flushList();
        elements.push(<h2 key={index} className="text-2xl font-semibold mb-4 mt-6">{line.substring(3)}</h2>);
      } else if (line.startsWith('### ')) {
        flushList();
        elements.push(<h3 key={index} className="text-xl font-medium mb-3 mt-4">{line.substring(4)}</h3>);
      }
      // Bold text with **
      else if (line.startsWith('- **') || line.startsWith('- ')) {
        inList = true;
        let text = line.substring(2);
        // Handle bold
        text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        listItems.push(<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />);
      }
      // Empty line
      else if (line.trim() === '') {
        flushList();
        elements.push(<div key={index} className="h-2" />);
      }
      // Regular paragraph
      else {
        flushList();
        let text = line;
        text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        elements.push(<p key={index} className="mb-3" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />);
      }
    });

    flushList();
    return elements;
  };

  const title = page?.title?.[language] || page?.title?.en || page?.title?.fr || '';
  const content = page?.content?.[language] || page?.content?.en || page?.content?.fr || '';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-20 pb-24 md:pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link to="/">
            <Button variant="ghost" className="mb-6 gap-2">
              <ArrowLeft className="h-4 w-4" />
              {language === 'fr' ? 'Retour' : 'Back'}
            </Button>
          </Link>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <Card>
              <CardContent className="py-20 text-center">
                <p className="text-muted-foreground">{error}</p>
                <Link to="/">
                  <Button className="mt-4">
                    {language === 'fr' ? 'Retour à l\'accueil' : 'Back to Home'}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-8 px-6 md:px-10">
                {/* Page Title */}
                {title && (
                  <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-8 pb-4 border-b">
                    {title}
                  </h1>
                )}
                
                <article className="prose prose-gray dark:prose-invert max-w-none">
                  {renderContent(content)}
                </article>
                
                {/* Last updated */}
                {page?.updated_at && (
                  <p className="text-sm text-muted-foreground mt-8 pt-4 border-t">
                    {language === 'fr' ? 'Dernière mise à jour : ' : 'Last updated: '}
                    {new Date(page.updated_at).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />
      <MobileNav />
    </div>
  );
}
